import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Coins, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { creditsAPI } from '../lib/api';
import { hasUgcDraft } from '../lib/ugcDraft';

export default function CreditSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch updated credit balance
    const fetchCredits = async () => {
      try {
        const response = await creditsAPI.getBalance();
        const data = response.data || response;
        setCredits(data);
      } catch (err) {
        console.error('Failed to fetch credits:', err);
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure webhook has processed
    setTimeout(fetchCredits, 2000);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Success Animation */}
        <div className="mb-8 animate-bounce-slow">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-500/20 rounded-full">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold text-white mb-2">
          Payment Successful!
        </h1>
        <p className="text-gray-400 mb-8">
          Your credits have been added to your account
        </p>

        {/* Credit Balance Card */}
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 mb-8">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : credits ? (
            <>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Coins className="w-8 h-8 text-yellow-500" />
                </div>
              </div>
              <div className="text-4xl font-bold text-white mb-1">
                {credits.total_available}
              </div>
              <div className="text-gray-400 text-sm">
                Total Credits Available
              </div>
              <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Purchased</div>
                  <div className="text-white font-medium">{credits.balance}</div>
                </div>
                <div>
                  <div className="text-gray-500">Daily Free</div>
                  <div className="text-white font-medium">{credits.daily_free_remaining}</div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-gray-400">Unable to load balance</p>
          )}
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          {hasUgcDraft() && (
            <Button
              data-testid="credit-success-back-to-ad-btn"
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-semibold"
              onClick={() => navigate('/ugc-studio?restore=1')}
            >
              Back to your UGC ad
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          <Button
            variant={hasUgcDraft() ? 'outline' : 'default'}
            className={hasUgcDraft() ? 'w-full border-gray-600 text-gray-300 hover:bg-gray-800' : 'w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'}
            onClick={() => navigate('/create')}
          >
            Start Creating
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button
            variant="outline"
            className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </div>

        {/* Support Link */}
        <p className="mt-8 text-sm text-gray-500">
          Need help?{' '}
          <a href="mailto:support@igoviralcreator.io" className="text-purple-400 hover:underline">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
